import React, { useCallback, useState } from 'react';
import Image from 'next/image';
import cn from 'classnames';
import { FileRejection, useDropzone } from 'react-dropzone';
import fileUpload from '@/public/icons/smartForm/fileUpload.svg';
import roundCheckMark from '@/public/icons/smartForm/roundCheckMark.svg';
import fileUploadChecked from '@/public/icons/smartForm/fileUploadChecked.svg';
import close from '@/public/icons/smartForm/close.svg';
import { MediaUploadZoneOldProps } from '@/lib/smartForm/types';
import i18n from '@/settings/i18n';

const t = i18n.init('shared');

const IMAGE = 'image';
const MULTIMEDIA = 'multimedia';
const MINIMUM = 'minimum';
const COMPLETE = 'complete';
const OPTIONAL = 'optional';
const FILE_SIZE_LIMIT = 10_485_760;

const acceptableImages = {
  'image/*': ['.jpg', '.png', '.gif'],
};
const acceptableVideo = {
  // 'video/*': ['.mp4'],
};

const defaultMediaUploadZoneOldProps = {
  showCounts: false,
};

function DesktopIndividualDropZone({
  acceptableFileType,
  onDrop,
}: {
  acceptableFileType: Record<string, Array<string>>;
  onDrop: (
    acceptedFiles: Array<File>,
    rejectedFiles: Array<FileRejection>,
  ) => void;
}) {
  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptableFileType,
    maxSize: FILE_SIZE_LIMIT,
    onDrop,
  });

  return (
    <div
      className="flex h-20 w-full flex-row justify-center rounded border-2 border-dashed border-gray-400/25 text-center align-middle"
      {...getRootProps()}
    >
      <input {...getInputProps()} />
      <Image
        src={fileUpload}
        alt={t`File Upload`}
        className="my-auto h-[40px] w-[40px] object-contain opacity-50"
      />
    </div>
  );
}

function MobileIndividualDropZone({
  acceptableFileType,
  onDrop,
}: {
  acceptableFileType: Record<string, Array<string>>;
  onDrop: (
    acceptedFiles: Array<File>,
    rejectedFiles: Array<FileRejection>,
  ) => void;
}) {
  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptableFileType,
    maxSize: FILE_SIZE_LIMIT,
    onDrop,
  });

  return (
    <div
      className="flex w-[90%] flex-row justify-center rounded border-2 border-dotted border-gray-400 px-1 py-1 text-left"
      {...getRootProps()}
    >
      <input {...getInputProps()} />
      <Image
        src={fileUpload}
        alt={t`File Upload`}
        width={30}
        height={30}
        className="my-auto h-[30px] w-[30px] object-contain opacity-50"
      />
    </div>
  );
}

function FinalDropZone({
  type,
  acceptableFileType,
  onDrop,
  files,
}: {
  type: MediaUploadZoneOldProps['type'];
  acceptableFileType: Record<string, Array<string>>;
  onDrop: (
    acceptedFiles: Array<File>,
    rejectedFiles: Array<FileRejection>,
  ) => void;
  files: Array<File>;
}) {
  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptableFileType,
    maxSize: FILE_SIZE_LIMIT,
    onDrop,
  });

  return (
    <div
      className={cn(
        'flex w-full flex-row',
        {
          'mt-8': files.length > 0,
        },
        {
          'sm:w-1/2': type === MULTIMEDIA && files.length === 0,
        },
      )}
    >
      {files.length > 0 && (
        <div className="flex w-1/2 justify-end text-right text-sm">
          <div className="w-full sm:w-[65%]">
            <p className="text-right text-gray-400/25 sm:my-2 sm:text-xs">
              {t`Additional pictures help us serve you better. Feel free to add more.`}
            </p>
          </div>
        </div>
      )}
      <div
        className={cn(
          'flex flex-col items-center justify-center border border-dashed border-gray-400 py-4 text-center',
          {
            'w-full':
              type === IMAGE || (type === MULTIMEDIA && files.length === 0),
          },
          {
            'h-32 sm:h-80 lg:h-72': type === MULTIMEDIA && files.length === 0,
          },
          {
            'w-2/3 rounded sm:w-1/2': files.length > 0,
          },
          {
            'ml-4': files.length > 0,
          },
        )}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <Image
          src={fileUpload}
          alt={t`File Upload`}
          className={cn(
            'inline-block h-[30px] w-[30px] object-contain sm:h-[40px] sm:w-[40px]',
            {
              'sm:!h-[30px] sm:!w-[30px]': files.length === 0,
            },
          )}
        />
        {files.length === 0 && (
          <div>
            <p className="my-1 text-xs font-light sm:text-sm">
              <span className="font-semibold">{t`Upload a file`} </span>
              <span className="hidden text-xs sm:inline sm:text-sm">{t`or drag & drop`}</span>
            </p>
            <p className="text-xs font-light sm:text-sm">{t`PNG, JPG, GIF up to 10MB`}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MediaUploadZoneOld(props: MediaUploadZoneOldProps) {
  const MediaUploadZoneProps: MediaUploadZoneOldProps = {
    ...defaultMediaUploadZoneOldProps,
    ...props,
  };
  const {
    type,
    descriptionType,
    showCounts,
    showTitle = true,
    showFooter = true,
    files,
    callback,
  } = MediaUploadZoneProps;
  const acceptableFileType =
    type === IMAGE
      ? acceptableImages
      : { ...acceptableImages, ...acceptableVideo };
  const [errorMessage, setErrorMessage] = useState<string>('');

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      const nextFiles = [...files, ...acceptedFiles];

      if (fileRejections.length !== 0) {
        if (
          fileRejections.some((rejection) =>
            rejection.errors.some((error) => error.code === 'file-too-large'),
          )
        ) {
          setErrorMessage(
            t`One or more files selected exceed the size limit of 10MB per file.`,
          );
        }
      } else {
        setErrorMessage('');
      }

      callback(nextFiles);
    },
    [files],
  );

  const onClickDelete = (idx: number) => {
    const currentFiles = [...files];

    currentFiles.splice(idx, 1);
    callback(currentFiles);
  };

  return (
    <div className="mb-6">
      <div>
        {showTitle && (
          <>
            <h5 className="block font-semibold text-gray-700 sm:mb-2 lg:text-base">
              {[MULTIMEDIA].includes(type) || files.length > 0
                ? t`Attach Pictures`
                : t`For cases of wrong accessory received, please attach a picture.`}
            </h5>
            <p className="mb-2 text-sm text-gray-400 sm:mb-4 sm:text-gray-700">
              {t`Please upload photos per the guidelines below — even if no visible fault is present. Missing or unclear photos may result in delays or the rejection of your return.`}
            </p>
          </>
        )}
        {errorMessage.length > 0 && (
          <div className="text-sm text-red">{errorMessage}</div>
        )}
        <div
          className={cn(
            'mb-10 flex',
            {
              'sm:flex-col':
                ([IMAGE].includes(type) &&
                  [MINIMUM, OPTIONAL].includes(descriptionType) &&
                  files.length === 0) ||
                ([MULTIMEDIA, IMAGE].includes(type) &&
                  [MINIMUM, COMPLETE, OPTIONAL].includes(descriptionType) &&
                  files.length > 0),
            },
            {
              'flex-col sm:flex-row':
                [MULTIMEDIA, IMAGE].includes(type) &&
                [MINIMUM, COMPLETE, OPTIONAL].includes(descriptionType) &&
                files.length >= 0,
            },
            {
              'sm:flex-row':
                type === MULTIMEDIA &&
                descriptionType === MINIMUM &&
                fileUpload.length === 0,
            },
          )}
        >
          {/* Initial State (No uploaded file) */}
          {files.length === 0 && (
            <div
              className={cn(
                'mb-4 mr-4 grid w-full flex-col text-left text-sm text-gray-400 sm:mb-0 sm:flex sm:w-1/2 sm:border sm:border-solid sm:border-gray-200 sm:p-6 sm:text-gray-700',
                { 'sm:hidden': type === IMAGE },
              )}
            >
              {[MULTIMEDIA].includes(type) && descriptionType === COMPLETE && (
                <h5 className="font-semibold">{t`Opened Package?`}</h5>
              )}
              {[MULTIMEDIA, IMAGE].includes(type) &&
                [MINIMUM, COMPLETE].includes(descriptionType) && (
                  <p className="text-sm text-gray-400 sm:text-gray-700">
                    {t`Please share minimum 4 pictures showing the front, back, sides of your device as well as connectors and buttons if applicable.`}
                  </p>
                )}
              {[MULTIMEDIA].includes(type) && descriptionType === COMPLETE && (
                <h5 className="mt-4 font-semibold">{t`Unsealed Package?`}</h5>
              )}
              {[MULTIMEDIA].includes(type) && descriptionType === COMPLETE && (
                <p className="text-sm text-gray-400 sm:text-gray-700">{t`Send us pictures of your unsealed parcel.`}</p>
              )}
              {[MULTIMEDIA].includes(type) && descriptionType === OPTIONAL && (
                <p className="text-sm">
                  {t`Please attach any photos which might help us understand your request better.`}
                </p>
              )}
              {[MULTIMEDIA].includes(type) && showCounts && (
                <div className="mb-4 mt-auto hidden sm:block">
                  <h1 className="h-1 text-center text-3xl font-bold ">
                    <span className="text-gray-700/25">{files.length}/</span>4
                  </h1>
                </div>
              )}
            </div>
          )}

          {/* with Files - Desktop */}
          {files.length > 0 && (
            <div className="hidden w-full flex-row sm:flex">
              <div className="mr-4 flex h-80 w-1/2 flex-col border border-solid border-gray-200 p-6 text-sm">
                <div className="hidden h-2/3 sm:block">
                  <p className="text-sm">
                    {t`Please share minimum 4 pictures showing the front, back, sides of your device as well as connectors and buttons if applicable.`}
                  </p>
                </div>
                <div className="mx-auto my-auto w-10">
                  <Image
                    src={roundCheckMark}
                    alt={roundCheckMark}
                    width={1}
                    height={1}
                    sizes="5%"
                    className={cn('object-contain', {
                      'opacity-50': files.length < 4,
                    })}
                  />
                  <h1 className="mt-1 h-1 text-center text-3xl font-bold">
                    {files.length}/{files.length <= 4 ? 4 : files.length}
                  </h1>
                </div>
              </div>
              <div className="mt-0.5 flex h-80 w-1/2 flex-col gap-5">
                {[...Array(4)].map((_, idx) => {
                  const file = files[idx] || undefined;

                  if (idx <= 4) {
                    if (file) {
                      return (
                        <div
                          key={idx}
                          className="flex h-16 w-full flex-row justify-center rounded border-2 border-solid border-gray-700 px-1 text-center align-middle"
                        >
                          <Image
                            src={fileUploadChecked}
                            alt={t`File Upload`}
                            width={35}
                            height={35}
                            className="my-auto inline-block h-full w-1/5  object-contain p-3"
                          />
                          <div className="my-auto w-[70%] px-2 text-left">
                            <p className="overflow-hidden text-xs">
                              {file.name}
                            </p>
                          </div>
                          <div
                            className="my-auto w-[10%] leading-none"
                            onClick={() => onClickDelete(idx)}
                          >
                            <Image
                              src={close}
                              alt={close}
                              width={15}
                              height={15}
                              className="object-contain"
                            />
                          </div>
                        </div>
                      );
                    }

                    return (
                      <DesktopIndividualDropZone
                        key={idx}
                        acceptableFileType={acceptableFileType}
                        onDrop={onDrop}
                      />
                    );
                  }

                  return null;
                })}
              </div>
            </div>
          )}
          {/* with Files - Mobile View */}
          {files.length > 0 && (
            <div className="flex w-full flex-col sm:hidden">
              <div className="mb-4 h-2/3">
                {/* <p className="text-sm text-gray-400 sm:text-gray-700">
                  {t`Please share minimum 4 pictures showing the front, back, sides of your device as well as connectors and buttons if applicable.`}
                </p> */}
                {[MULTIMEDIA].includes(type) &&
                  descriptionType === COMPLETE && (
                    <h5 className="text-sm font-semibold text-gray-400">{t`Opened Package?`}</h5>
                  )}
                {[MULTIMEDIA, IMAGE].includes(type) &&
                  [MINIMUM, COMPLETE].includes(descriptionType) && (
                    <p className="text-sm text-gray-400">
                      {t`Please share minimum 4 pictures showing the front, back, sides of your device as well as connectors and buttons if applicable.`}
                    </p>
                  )}
                {[MULTIMEDIA].includes(type) &&
                  descriptionType === COMPLETE && (
                    <h5 className="mt-4 text-sm font-semibold text-gray-400">{t`Unsealed Package?`}</h5>
                  )}
                {[MULTIMEDIA].includes(type) &&
                  descriptionType === COMPLETE && (
                    <p className="text-sm text-gray-400">{t`Send us pictures of your unsealed parcel.`}</p>
                  )}
              </div>
              {[...Array(4)].map((_, idx) => {
                const file = files[idx] || undefined;

                if (idx <= 4) {
                  if (file) {
                    return (
                      <div key={idx} className="mb-2 flex h-10 w-full flex-row">
                        <div className="mr-2 h-10 w-[10%] rounded border-2 border-solid border-gray-700 bg-gray-700 indent-0 align-middle text-white">
                          <p className="my-2 text-center text-sm">
                            {idx + 1}/{files.length <= 4 ? 4 : files.length}
                          </p>
                        </div>
                        <div className="flex w-[90%] flex-row justify-center rounded border-2 border-solid border-gray-700 px-1 py-1 text-left">
                          <Image
                            src={fileUploadChecked}
                            alt={t`File Upload`}
                            className="my-auto h-[30px] w-[30px] object-contain"
                          />
                          <div className="my-auto w-[84%] px-2 text-left">
                            <p className="overflow-hidden truncate text-sm">
                              {file.name}
                            </p>
                          </div>
                          <div
                            className="my-auto w-[6%] leading-none"
                            onClick={() => onClickDelete(idx)}
                          >
                            <Image
                              src={close}
                              alt={close}
                              width={15}
                              height={15}
                              className="object-contain"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={idx} className="mb-2 flex h-10 w-full flex-row">
                      <MobileIndividualDropZone
                        key={idx}
                        acceptableFileType={acceptableFileType}
                        onDrop={onDrop}
                      />
                    </div>
                  );
                }

                return null;
              })}
            </div>
          )}
          {/* Drag and Drop Zone - main  */}
          <FinalDropZone
            acceptableFileType={acceptableFileType}
            type={type}
            onDrop={onDrop}
            files={files}
          />
        </div>
        {showFooter && type === IMAGE && files.length === 0 && (
          <p className="mt-6 text-sm sm:mt-12">
            {t`We will do our best to send you your missing accessory. If it is not available, you will instead receive a fair compensation for the missing part.`}
          </p>
        )}
      </div>
    </div>
  );
}
